import type { ReactNode } from 'react';
import { BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TeacherDetail } from '../types/teacherSubmission.types';
import { TeacherDetailsTable, TeacherDetailsTableSkeleton } from './TeacherDetailsTable';
import { TeacherExpertiseGrid, TeacherExpertiseGridSkeleton } from './TeacherExpertiseGrid';
import { TeacherInfoCard, TeacherInfoCardSkeleton } from './TeacherInfoCard';
import { TeacherProfileCard, TeacherProfileCardSkeleton } from './TeacherProfileCard';

const PROFILE_COLUMNS_CLASS = 'grid items-start gap-4 @2xl:grid-cols-[18rem_minmax(0,1fr)]';
const PROFILE_CARD_STICKY_CLASS = 'lg:sticky lg:top-0';
const PROFILE_CARD_BESIDE_HEADER_CLASS = '@2xl:col-start-1 @2xl:row-span-2 @2xl:row-start-1';
const RIGHT_COLUMN_CLASS = '@2xl:col-start-2';
const DETAIL_CARDS_GRID_CLASS = 'grid min-w-0 content-start items-start gap-4';
const SKELETON_REVEAL_CLASS =
  'motion-safe:animate-in fade-in-0 duration-200 delay-150 ease-out fill-mode-both';
const CARD_REVEAL_CLASS =
  'motion-safe:animate-in fade-in-0 slide-in-from-bottom-2 duration-300 ease-out fill-mode-both';
const CARD_REVEAL_DELAY_CLASS = {
  profile: 'delay-0',
  details: 'delay-50',
  expertise: 'delay-100',
} as const;
const EXPERTISE_ROW_COUNT = 3;

type CardRevealStep = keyof typeof CARD_REVEAL_DELAY_CLASS;

const getCardRevealClass = (step: CardRevealStep): string =>
  cn(CARD_REVEAL_CLASS, CARD_REVEAL_DELAY_CLASS[step]);

interface ProfileColumnsProps {
  detailsHeader: ReactNode;
  profileCard: ReactNode;
  detailCards: ReactNode;
}

function ProfileColumns({ detailsHeader, profileCard, detailCards }: ProfileColumnsProps) {
  const hasDetailsHeader = detailsHeader != null;

  return (
    <div className={PROFILE_COLUMNS_CLASS}>
      {hasDetailsHeader && <div className={cn('min-w-0', RIGHT_COLUMN_CLASS)}>{detailsHeader}</div>}
      <div
        className={cn(
          PROFILE_CARD_STICKY_CLASS,
          hasDetailsHeader && PROFILE_CARD_BESIDE_HEADER_CLASS,
        )}
      >
        {profileCard}
      </div>
      <div className={cn(DETAIL_CARDS_GRID_CLASS, hasDetailsHeader && RIGHT_COLUMN_CLASS)}>
        {detailCards}
      </div>
    </div>
  );
}

interface TeacherProfileDetailsProps {
  teacher: TeacherDetail;
  detailsHeader?: ReactNode;
  onShowOnlyThisInstitute?: (() => void) | undefined;
}

export function TeacherProfileDetails({
  teacher,
  detailsHeader,
  onShowOnlyThisInstitute,
}: TeacherProfileDetailsProps) {
  return (
    <ProfileColumns
      detailsHeader={detailsHeader}
      profileCard={
        <TeacherProfileCard
          teacher={teacher}
          onShowOnlyThisInstitute={onShowOnlyThisInstitute}
          className={getCardRevealClass('profile')}
        />
      }
      detailCards={
        <>
          <TeacherDetailsTable teacher={teacher} className={getCardRevealClass('details')} />
          <TeacherInfoCard
            icon={BookOpen}
            title="Classes and subjects"
            bodyClassName="p-0"
            className={getCardRevealClass('expertise')}
          >
            <TeacherExpertiseGrid teacher={teacher} />
          </TeacherInfoCard>
        </>
      }
    />
  );
}

interface TeacherProfileDetailsSkeletonProps {
  detailsHeader?: ReactNode;
}

export function TeacherProfileDetailsSkeleton({
  detailsHeader,
}: TeacherProfileDetailsSkeletonProps) {
  return (
    <div aria-busy className={SKELETON_REVEAL_CLASS}>
      <ProfileColumns
        detailsHeader={detailsHeader}
        profileCard={<TeacherProfileCardSkeleton />}
        detailCards={
          <>
            <TeacherDetailsTableSkeleton />
            <TeacherInfoCardSkeleton bodyClassName="p-0">
              <TeacherExpertiseGridSkeleton rowCount={EXPERTISE_ROW_COUNT} />
            </TeacherInfoCardSkeleton>
          </>
        }
      />
    </div>
  );
}
